/**
 * run-checkbox-group — Default skin module
 *
 * Exports { styles, render } per SkinModule contract.
 * render(host) writes shadow DOM to host.shadowRoot.
 * styles is a CSS string; run-checkbox-group.js creates the CSSStyleSheet from it.
 *
 * Reads host properties read-only. Never writes host state.
 * Token disposition: pass-through (no overrides)
 */

export const styles = `
  *, *::before, *::after { box-sizing: border-box; }

  :host {
    display: block;
  }

  :host([disabled]) {
    opacity: var(--run-checkbox-group-disabled-opacity, 0.4);
    pointer-events: none;
  }

  [part="base"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-checkbox-group-label-gap, 4px);
  }

  [part="base"][data-invalid] {
    border: 1px solid var(--run-color-intent-danger-base, #dc2626);
    border-radius: var(--run-radius-semantic-control, 4px);
    padding: var(--run-space-semantic-sm, 8px);
  }

  [part="label"] {
    color: var(--run-color-text-primary, #1a1a1a);
    font-weight: 500;
  }

  [part="label"]:empty {
    display: none;
  }

  .select-all-container {
    padding-bottom: var(--run-checkbox-group-label-gap, 4px);
    border-bottom: 1px solid var(--run-color-border-subtle, #e5e5e5);
    margin-bottom: var(--run-checkbox-group-label-gap, 4px);
  }

  .select-all-container:empty {
    display: none;
  }

  [part="children"] {
    display: flex;
    gap: var(--run-checkbox-group-gap, 8px);
  }

  [part="children"][data-orientation="vertical"] {
    flex-direction: column;
  }

  [part="children"][data-orientation="horizontal"] {
    flex-direction: row;
    flex-wrap: wrap;
  }

  [part="error"] {
    color: var(--run-color-feedback-danger-foreground, #dc2626);
    font-size: 0.875em;
  }

  [part="error"]:not([data-visible]) {
    display: none;
  }
`;

export function render(host) {
  const sr = host.shadowRoot;

  // Create skeleton only on first render
  if (!sr.querySelector('[part="base"]')) {
    sr.innerHTML = `
      <div part="base">
        <div part="label" id="${host.localName}-label-${Date.now()}">
          <slot name="label"></slot>
        </div>
        <div class="select-all-container">
          <slot name="select-all"></slot>
        </div>
        <div part="children">
          <slot></slot>
        </div>
        <div part="error">
          <slot name="error"></slot>
        </div>
      </div>`;
  }

  // Patch attributes on subsequent calls
  const base = sr.querySelector('[part="base"]');
  const children = sr.querySelector('[part="children"]');
  const error = sr.querySelector('[part="error"]');

  // Data attributes on base for CSS targeting
  base.setAttribute('data-orientation', host.orientation || 'vertical');
  base.toggleAttribute('data-disabled', !!host.disabled);
  base.toggleAttribute('data-readonly', !!host.readonly);
  base.toggleAttribute('data-invalid', !!host.invalid);
  base.toggleAttribute('data-required', !!host.required);

  // Orientation on children container
  children.setAttribute('data-orientation', host.orientation || 'vertical');

  // Error visibility
  error.toggleAttribute('data-visible', !!host.invalid);
}
